import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card, CardBody, Col, Form, Input, Label, Row, FormFeedback, Spinner, Container, CardHeader
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useConfig } from "../../Context/ConfigContext";

interface FormValues {
    vehicle_id: string;
    driver_id: string;
    incident_severity: string;
    incident_date_time: Date;
    incident_location: string;
    incident_description: string;

    // Cost
    estimated_repair_cost: number;
    actual_repair_cost: number;

    // Insurance
    insurance_policy_number: string;
    insurance_provider: string;
    claim_filed_date: Date;
    claim_status: string;
    claim_number: string;
    approved_claim_amount: number;

    // Status
    accident_status: string;

    // Documents
    accident_photos: FileList;
    police_report: FileList;
    repair_invoice: FileList;
    payment_receipt: FileList;
    insurance_claim_report: FileList;
}

export default function AddAccident() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [drivers, setDrivers] = useState<any[]>([]);

    const { handleSubmit, control, setValue, watch, formState: { errors } } = useForm<FormValues>();
    const watchStatus = watch("accident_status");
    const { config } = useConfig();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const vehicleRes = await apiPost("vehicles/getall");
                const activeVehicles = Array.isArray(vehicleRes) ? vehicleRes.filter((v: any) => v.status !== 'Sold' && v.status !== 'Destroyed') : [];
                setVehicles(activeVehicles); // Assuming full list or modify endpoint
                const driverRes = await apiPost("drivers/getall");
                setDrivers(driverRes);
            } catch (e) {
                console.error("Error fetching options", e);
            }
        };
        fetchData();
    }, []);

    const onSubmit = async (data: FormValues) => {
        setLoading(true);
        try {
            // Handle File Uploads
            let policeReportName = "";
            let repairInvoiceName = "";
            let paymentReceiptName = "";
            let insuranceClaimReportName = "";
            const accidentPhotoNames = [];

            if (data.police_report && data.police_report.length > 0) {
                policeReportName = await uploadFile(data.police_report[0]);
            }
            if (data.repair_invoice && data.repair_invoice.length > 0) {
                repairInvoiceName = await uploadFile(data.repair_invoice[0]);
            }
            if (data.payment_receipt && data.payment_receipt.length > 0) {
                paymentReceiptName = await uploadFile(data.payment_receipt[0]);
            }
            if (data.insurance_claim_report && data.insurance_claim_report.length > 0) {
                insuranceClaimReportName = await uploadFile(data.insurance_claim_report[0]);
            }

            if (data.accident_photos && data.accident_photos.length > 0) {
                for (let i = 0; i < data.accident_photos.length; i++) {
                    const name = await uploadFile(data.accident_photos[i]);
                    accidentPhotoNames.push(name);
                }
            }

            const payload = {
                ...data,
                incident_date_time: data.incident_date_time ? data.incident_date_time.toISOString().slice(0, 19).replace('T', ' ') : null,
                claim_filed_date: data.claim_filed_date ? data.claim_filed_date.toISOString().slice(0, 10) : null,
                police_report: policeReportName,
                repair_invoice: repairInvoiceName,
                payment_receipt: paymentReceiptName,
                insurance_claim_report: insuranceClaimReportName,
                accident_photos: accidentPhotoNames // Backend expects array, will cast to JSON
            };

            await apiPost("accidents/store", payload);
            toast.success("Accident reported successfully", { autoClose: 1000 });
            navigate(`${process.env.PUBLIC_URL}/accident`);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "An unexpected error occurred");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Accident" parent="Safety" title="Report Accident" />
            <Container fluid>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                        <CardHeader tag="h5" style={{
                            backgroundColor: "#f8f9fa",
                            borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px"
                        }}>
                            Incident Details
                        </CardHeader>
                        <CardBody>
                            <Row className="g-3 mb-4">
                                <Col md={4}>
                                    <Label>Vehicle <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="vehicle_id"
                                        control={control}
                                        rules={{ required: "Vehicle is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input
                                                    type="select"
                                                    {...field}
                                                    invalid={!!errors.vehicle_id}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        const selectedVehicleId = e.target.value;
                                                        const vehicle = vehicles.find((v: any) => String(v.id) === String(selectedVehicleId));
                                                        if (vehicle && vehicle.assigned_driver_id) {
                                                            setValue("driver_id", vehicle.assigned_driver_id);
                                                        }
                                                    }}
                                                >
                                                    <option value="">Select Vehicle</option>
                                                    {vehicles.map((v: any) => <option key={v.id} value={v.id}>{v.registration_number} - {v.model}</option>)}
                                                </Input>
                                                <FormFeedback>{errors.vehicle_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Driver <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="driver_id"
                                        control={control}
                                        rules={{ required: "Driver is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.driver_id}>
                                                    <option value="">Select Driver</option>
                                                    {drivers.map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                                </Input>
                                                <FormFeedback>{errors.driver_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Incident Date & Time <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="incident_date_time"
                                        control={control}
                                        rules={{ required: "Date/Time is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.incident_date_time ? "is-invalid" : ""}`}
                                                    selected={field.value}
                                                    onChange={(date) => field.onChange(date)}
                                                    showTimeSelect
                                                    dateFormat="yyyy-MM-dd HH:mm"
                                                    placeholderText="YYYY-MM-DD HH:MM"
                                                    autoComplete="off"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                                <FormFeedback className={errors.incident_date_time ? "d-block" : ""}>{errors.incident_date_time?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Severity <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="incident_severity"
                                        control={control}
                                        rules={{ required: "Severity is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.incident_severity}>
                                                    <option value="">Select Severity</option>
                                                    <option value="Minor">Minor</option>
                                                    <option value="Moderate">Moderate</option>
                                                    <option value="Major">Major</option>
                                                    <option value="Total Loss">Total Loss</option>
                                                </Input>
                                                <FormFeedback>{errors.incident_severity?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Location <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="incident_location"
                                        control={control}
                                        rules={{ required: "Location is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Location" invalid={!!errors.incident_location} autoComplete="off" />
                                                <FormFeedback>{errors.incident_location?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Status <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="accident_status"
                                        control={control}
                                        defaultValue="Open"
                                        rules={{ required: "Status is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.accident_status}>
                                                    <option value="Open">Open</option>
                                                    <option value="In Progress">In Progress</option>
                                                    <option value="Closed">Closed</option>
                                                </Input>
                                                <FormFeedback>{errors.accident_status?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={12}>
                                    <Label>Description</Label>
                                    <Controller
                                        name="incident_description"
                                        control={control}
                                        render={({ field }) => <Input type="textarea" {...field} placeholder="Describe the incident details..." autoComplete="off" />}
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                        <CardHeader tag="h5" style={{
                            backgroundColor: "#f8f9fa",
                            borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px"
                        }}>
                            Cost & Insurance
                        </CardHeader>
                        <CardBody>
                            <Row className="g-3 mb-4">
                                <Col md={3}>
                                    <Label>Est. Repair Cost ({config.currency})</Label>
                                    <Controller
                                        name="estimated_repair_cost"
                                        control={control}
                                        render={({ field }) => <Input type="number" {...field} placeholder={`0.00 (${config.currency})`} autoComplete="off" />}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Actual Repair Cost ({config.currency}) {watchStatus === 'Closed' && <span className="text-danger">*</span>}</Label>
                                    <Controller
                                        name="actual_repair_cost"
                                        control={control}
                                        rules={{ required: watchStatus === 'Closed' ? "Actual cost is required" : false }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="number" {...field} placeholder={`0.00 (${config.currency})`} invalid={!!errors.actual_repair_cost} autoComplete="off" />
                                                <FormFeedback>{errors.actual_repair_cost?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Approved Claim ({config.currency})</Label>
                                    <Controller
                                        name="approved_claim_amount"
                                        control={control}
                                        render={({ field }) => <Input type="number" {...field} placeholder={`0.00 (${config.currency})`} autoComplete="off" />}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Claim Status</Label>
                                    <Controller
                                        name="claim_status"
                                        control={control}
                                        defaultValue="Not Filed"
                                        render={({ field }) => (
                                            <Input type="select" {...field}>
                                                <option value="Not Filed">Not Filed</option>
                                                <option value="Filed">Filed</option>
                                                <option value="Under Review">Under Review</option>
                                                <option value="Approved">Approved</option>
                                                <option value="Rejected">Rejected</option>
                                                <option value="Settled">Settled</option>
                                            </Input>
                                        )}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Claim Number</Label>
                                    <Controller
                                        name="claim_number"
                                        control={control}
                                        render={({ field }) => <Input {...field} placeholder="Claim No." autoComplete="off" />}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Label>Insurance Provider</Label>
                                    <Controller name="insurance_provider" control={control} render={({ field }) => <Input {...field} placeholder="Provider Name" autoComplete="off" />} />
                                </Col>
                                <Col md={3}>
                                    <Label>Policy Number</Label>
                                    <Controller name="insurance_policy_number" control={control} render={({ field }) => <Input {...field} placeholder="Policy No." autoComplete="off" />} />
                                </Col>
                                <Col md={3}>
                                    <Label>Claim Filed Date</Label>
                                    <Controller
                                        name="claim_filed_date"
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                                className="form-control"
                                                selected={field.value}
                                                onChange={(date) => field.onChange(date)}
                                                placeholderText="YYYY-MM-DD"
                                                dateFormat="yyyy-MM-dd"
                                                autoComplete="off"
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="select"
                                            />
                                        )}
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                        <CardHeader tag="h5" style={{
                            backgroundColor: "#f8f9fa",
                            borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px"
                        }}>
                            Documents
                        </CardHeader>
                        <CardBody>
                            <Row className="g-3 mb-4">
                                <Col md={6}>
                                    <Label>Accident Photos (Multiple) {watchStatus === 'Closed' && <span className="text-danger">*</span>}</Label>
                                    <Controller
                                        name="accident_photos"
                                        control={control}
                                        rules={{ required: watchStatus === 'Closed' ? "Photos are required" : false }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    multiple
                                                    accept="image/*"
                                                    onChange={(e) => onChange(e.target.files)}
                                                    invalid={!!errors.accident_photos}
                                                />
                                                <FormFeedback>{errors.accident_photos?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Police Report</Label>
                                    <Controller
                                        name="police_report"
                                        control={control}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <Input type="file" {...field} onChange={(e) => onChange(e.target.files)} accept=".pdf,image/*" />
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Repair Invoice {watchStatus === 'Closed' && <span className="text-danger">*</span>}</Label>
                                    <Controller
                                        name="repair_invoice"
                                        control={control}
                                        rules={{ required: watchStatus === 'Closed' ? "Invoice is required" : false }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input type="file" {...field} onChange={(e) => onChange(e.target.files)} accept=".pdf,image/*" invalid={!!errors.repair_invoice} />
                                                <FormFeedback>{errors.repair_invoice?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Payment Receipt {watchStatus === 'Closed' && <span className="text-danger">*</span>}</Label>
                                    <Controller
                                        name="payment_receipt"
                                        control={control}
                                        rules={{ required: watchStatus === 'Closed' ? "Receipt is required" : false }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input type="file" {...field} onChange={(e) => onChange(e.target.files)} accept=".pdf,image/*" invalid={!!errors.payment_receipt} />
                                                <FormFeedback>{errors.payment_receipt?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Insurance Claim Report</Label>
                                    <Controller
                                        name="insurance_claim_report"
                                        control={control}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <Input type="file" {...field} onChange={(e) => onChange(e.target.files)} accept=".pdf,image/*" />
                                        )}
                                    />
                                </Col>
                            </Row>


                        </CardBody>

                    </Card>
                    <div className="text-start">
                        <Btn color="primary" disabled={loading} type="submit">
                            {loading ? <>Submitting <Spinner size="sm" /></> : "Submit"}
                        </Btn>
                    </div>
                </Form>
            </Container>
            <ToastContainer />
        </div>
    );
}
